/**
 * Session-expired modal.
 *
 * Subscribes to the global `session-expired` event dispatched by `api-client`
 * when a refresh attempt fails. Behaviour:
 *
 * 1. As soon as the event fires we drop the in-memory token (fix for review
 *    finding Major-5 — the previous version only did this when the user
 *    clicked the modal action, leaving stale state visible in the meantime).
 * 2. We open a non-dismissable modal explaining the session has ended.
 * 3. On user action we navigate to `/login?return_to=<safe path>` so the user
 *    lands back where they were after re-authentication.
 *
 * `safeReturnTo` rejects anything that looks like an open-redirect vector
 * (absolute URLs, protocol-relative, CRLF injection). Fix for review finding
 * Major-3.
 */

import { useEffect, useState } from 'react';
import { Button, Modal, Stack, Text } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-provider';
import { SESSION_EXPIRED_EVENT } from '@/lib/api-client';

export function safeReturnTo(raw: string | null | undefined): string {
  if (!raw) return '/';
  if (typeof raw !== 'string') return '/';
  if (!raw.startsWith('/')) return '/';
  if (raw.startsWith('//')) return '/'; // protocol-relative
  if (raw.includes('://')) return '/'; // absolute URL injection
  if (raw.includes('\\')) return '/'; // backslash tricks
  if (/[\r\n]/.test(raw)) return '/'; // CRLF
  if (raw.startsWith('/login')) return '/'; // do not loop back to login
  return raw;
}

export function SessionExpiredModal() {
  const [opened, setOpened] = useState(false);
  const { clearSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handler = () => {
      // Major-5: drop token *now*, not on click. Prevents stale UI flashes.
      clearSession();
      setOpened(true);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
  }, [clearSession]);

  const onSignInAgain = () => {
    setOpened(false);
    const returnTo = safeReturnTo(location.pathname + location.search);
    const search = returnTo === '/' ? '' : `?return_to=${encodeURIComponent(returnTo)}`;
    navigate(`/login${search}`, { replace: true });
  };

  return (
    <Modal
      opened={opened}
      onClose={() => undefined /* not dismissable — must re-auth */}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      centered
      title="Сессия истекла"
    >
      <Stack gap="md">
        <Text size="sm">
          Срок действия сессии истёк. Войдите снова, чтобы продолжить работу.
        </Text>
        <Button onClick={onSignInAgain} fullWidth>
          Войти снова
        </Button>
      </Stack>
    </Modal>
  );
}
